import React, { memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { GSIcon } from 'src/components/shared/GSIcon'
import { ONBOARDING_STEPS, OnboardingStep } from 'src/constants/payments-ops/bankOnboarding'

interface Props {
  currentStep: OnboardingStep
  completedSteps: OnboardingStep[]
  /** Freezes navigation — set while the start request is in flight. */
  isLocked?: boolean
  onStepClick(step: OnboardingStep): void
}

/**
 * Numbered pills; a completed step shows a check and is clickable in either direction. Going
 * forward is still gated: the wizard validates the step being left before it jumps.
 */
const OnboardingStepperComponent = ({
  currentStep,
  completedSteps,
  isLocked = false,
  onStepClick,
}: Props) => {
  return (
    // Same card chrome as the step's `SectionCard` below it, so the pills do not float on the grey
    // canvas.
    <ol
      css={tw`m-0 flex shrink-0 list-none flex-row flex-wrap gap-2 rounded-lg border border-solid border-shades-100 bg-shades-0 p-4`}
      aria-label="Onboarding steps"
    >
      {ONBOARDING_STEPS.map(({ step, title }, index) => {
        const isCurrent = step === currentStep
        const isCompleted = completedSteps.includes(step)
        const isClickable = isCompleted && !isCurrent && !isLocked
        let titleCss = tw`text-shades-500`

        // Colour is always explicit: a disabled <button> would otherwise grey its text out.
        if (isCurrent) {
          titleCss = tw`font-semibold text-primary-500`
        } else if (isCompleted) {
          titleCss = tw`font-semibold text-shades-1000`
        }

        return (
          <li key={step}>
            <button
              type="button"
              disabled={!isClickable}
              aria-current={isCurrent ? 'step' : undefined}
              onClick={() => {
                onStepClick(step)
              }}
              data-testid={`onboarding-step-${step}`}
              css={[
                tw`flex flex-row items-center gap-2 rounded-full border border-solid px-3 py-1.5`,
                isCurrent
                  ? tw`border-primary-500 bg-primary-100`
                  : tw`border-shades-100 bg-shades-0`,
                isClickable ? tw`cursor-pointer` : tw`cursor-default`,
              ]}
            >
              {isCompleted ? (
                <GSIcon iconName="gs_circle_check" size={16} css={tw`text-success-500`} />
              ) : (
                <Typography variant="body2" css={[tw`text-xs font-semibold`, titleCss]}>
                  {index + 1}
                </Typography>
              )}
              <Typography variant="body2" css={titleCss}>
                {title}
              </Typography>
            </button>
          </li>
        )
      })}
    </ol>
  )
}

export const OnboardingStepper = memo(OnboardingStepperComponent)
